import { useState } from "react";
import { motion } from "framer-motion";
import { AudioLines, ArrowUpRight, BookOpen, MessageCircle, Music } from "lucide-react";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import Modal from "./Modal";
import { books, poems, songs, whatsappBookLink } from "../../data/content";
import { scrollToSection } from "../../lib/scroll";

const Books = () => {
    const [active, setActive] = useState(null);

    const listenSong = (poemId) => {
        const song = songs.find((s) => s.poemId === poemId);
        if (!song) return;
        setActive(null);
        scrollToSection("#canciones");
        window.dispatchEvent(new CustomEvent("play-song", { detail: song.id }));
    };

    return (
        <section id="libros" data-testid="books-section" className="py-24 sm:py-36 bg-cream border-y border-line">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <SectionHeading
                    kicker="02 — Obra impresa"
                    title="Los cuatro…"
                    description="De Instantáneas (2012) a El hombre de las tres guerras (2022): una década de poesía publicada entre Quito y Aveiro (Portugal)."
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
                    {books.map((book, i) => (
                        <Reveal key={book.id} delay={i * 0.1}>
                            <motion.article
                                data-testid={`book-card-${i}`}
                                className="group h-full flex flex-col"
                                whileHover={{ y: -10 }}
                                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                            >
                                <div className="relative border border-line bg-white p-4 shadow-md group-hover:shadow-2xl transition-shadow duration-500">
                                    <span className="absolute top-6 left-6 z-10 bg-ink text-paper font-mono text-[10px] tracking-[0.2em] px-2 py-1">
                                        {book.year}
                                    </span>
                                    <div className="aspect-[3/4] overflow-hidden p-6 sm:p-8">
                                        <img
                                            src={book.cover}
                                            alt={`Portada de ${book.title}`}
                                            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.04]"
                                        />
                                    </div>
                                </div>
                                <div className="pt-5 flex-1 flex flex-col">
                                    <h3 className="font-display text-2xl leading-tight text-ink">
                                        {book.title}
                                    </h3>
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">
                                        {book.publisher}
                                    </p>
                                    <p className="mt-3 text-base leading-relaxed text-ink/75 flex-1">
                                        {book.description}
                                    </p>
                                    {book.spotify && (
                                        <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-golddeep">
                                            <AudioLines size={12} />
                                            Con audio en Spotify
                                        </p>
                                    )}
                                    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                                        <button
                                            data-testid={`book-preview-button-${i}`}
                                            onClick={() => setActive(book)}
                                            className="flex items-center gap-2 border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:text-crimson hover:border-crimson transition-colors"
                                        >
                                            <BookOpen size={13} />
                                            Ver más
                                        </button>
                                        <a
                                            data-testid={`book-buy-button-${i}`}
                                            href={whatsappBookLink(book.title)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 border-b border-crimson pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-crimson hover:text-ink hover:border-ink transition-colors"
                                        >
                                            <MessageCircle size={13} />
                                            Comprar libro
                                        </a>
                                    </div>
                                </div>
                            </motion.article>
                        </Reveal>
                    ))}
                </div>
            </div>

            <Modal
                open={!!active}
                onClose={() => setActive(null)}
                label={active ? `Índice de ${active.title}` : ""}
                testId="book-index-modal"
            >
                {active && (
                    <div className="p-8 sm:p-12">
                        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                            {active.publisher} · {active.year}
                        </p>
                        <h3 className="mt-3 font-display text-3xl sm:text-4xl text-ink">
                            {active.title}
                        </h3>
                        <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-subtle">
                            {active.meta}
                        </p>
                        {active.contra && (
                            <blockquote
                                data-testid="book-contra-text"
                                className="mt-8 border-l-2 border-crimson pl-5"
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                                    {active.contra.label}
                                </p>
                                <p className="mt-3 font-display italic text-lg sm:text-xl leading-relaxed text-ink/80">
                                    «{active.contra.quote}»
                                </p>
                                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                    — {active.contra.author}
                                </p>
                            </blockquote>
                        )}
                        <div className="mt-8 border-t border-line pt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                            {active.index.map((poemTitle, i) => {
                                const linked = (active.previewPoemIds || [])
                                    .map((pid) => poems.find((p) => p.id === pid))
                                    .find((p) => p && p.title === poemTitle);
                                return (
                                    <div
                                        key={i}
                                        className="flex items-baseline justify-between gap-3 py-2 border-b border-line/60"
                                    >
                                        <div className="flex items-baseline gap-3">
                                            <span className="font-mono text-[10px] text-crimson w-6 shrink-0">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="font-display text-lg text-ink/85">
                                                {poemTitle}
                                            </span>
                                        </div>
                                        {linked && (
                                            <div className="shrink-0 flex flex-col items-end gap-1">
                                                <a
                                                    data-testid={`book-index-poem-link-${linked.id}`}
                                                    href={`/poema/${linked.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-crimson hover:text-ink transition-colors"
                                                >
                                                    Leer poema
                                                    <ArrowUpRight size={11} />
                                                </a>
                                                {songs.some((s) => s.poemId === linked.id) && (
                                                    <button
                                                        data-testid={`book-index-song-${linked.id}`}
                                                        onClick={() => listenSong(linked.id)}
                                                        className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-golddeep hover:text-ink transition-colors"
                                                    >
                                                        <Music size={10} />
                                                        Escuchar canción
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {active.partialIndex && (
                            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                Selección — la edición impresa incluye más poemas
                            </p>
                        )}
                        <div className="mt-8">
                            <a
                                data-testid="book-modal-buy-button"
                                href={whatsappBookLink(active.title)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2.5 bg-crimson text-paper px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-ink transition-colors duration-300"
                            >
                                <MessageCircle size={14} />
                                Comprar libro por WhatsApp
                            </a>
                        </div>
                        {active.spotify && (
                            <div className="mt-10 border-t border-line pt-8">
                                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                                    <AudioLines size={13} />
                                    Escucha los poemas en Spotify
                                </p>
                                <div
                                    data-testid="book-spotify-embed"
                                    className="mt-5 overflow-hidden rounded-xl border border-line"
                                >
                                    <iframe
                                        src={
                                            active.spotifyAlbum
                                                ? `https://open.spotify.com/embed/album/${active.spotifyAlbum}`
                                                : `https://open.spotify.com/embed/artist/${active.spotify}`
                                        }
                                        title={`Spotify: poemas de ${active.title}`}
                                        width="100%"
                                        height="352"
                                        style={{ border: 0, display: "block" }}
                                        loading="lazy"
                                        allowFullScreen
                                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    />
                                </div>
                                <p className="mt-3 font-mono text-[10px] tracking-[0.12em] text-subtle">
                                    Si no estás conectado a Spotify, escucharás una vista previa de cada poema
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default Books;
